<template>
  <div>
    <v-card flat>
      <v-data-table
        :headers="headers"
        :items="filteredMedications"
        :items-per-page="10"
        class="elevation-0 custom-data-table"
        :no-data-text="noDataText"
        :height="tableHeight"
        fixed-header
        :loading="tableLoading"
        density="compact"
      >
        <template v-slot:top>
          <div class="d-flex justify-space-between align-center pa-4 flex-wrap ga-3">
            <span class="text-h5">Medications</span>
            <div class="d-flex align-center flex-wrap ga-3">
              <v-select
                v-model="selectedCategoryId"
                :items="categoryOptions"
                :item-title="(c) => formatMedicationCategoryName(c.category_name)"
                item-value="category_id"
                label="Category"
                clearable
                hide-details
                variant="outlined"
                density="compact"
                style="width: 180px"
                @update:model-value="onCategoryChange"
             />

              <v-select
                v-if="hasMedicationClasses"
                v-model="selectedSubCategoryId"
                :items="filteredMedicationClassOptions"
                item-title="class_name"
                item-value="class_id"
                label="Medication Class"
                clearable
                hide-details
                variant="outlined"
                density="compact"
                style="min-width: 260px; width: 260px"
              />

              <v-text-field
                v-model="search"
                prepend-inner-icon="mdi-magnify"
                label="Search"
                single-line
                hide-details
                variant="outlined"
                density="compact"
                style="width: 250px"
                />

              <v-btn
                color="primary"
                prepend-icon="mdi-plus"
                @click="openCreateDialog"
              >
                Create Medication
              </v-btn>
            </div>
          </div>
        </template>

        <template v-slot:[`item.medication_category_name`]="{ item }">
          {{ formatMedicationCategoryName(item.medication_category_name) }}
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <v-btn
            icon
            size="small"
            variant="text"
            color="primary"
            @click="editMedication(item)"
          >
            <v-icon>mdi-pencil</v-icon>
          </v-btn>
          <v-btn
            icon
            size="small"
            variant="text"
            color="error"
            @click="deleteMedication(item)"
          >
            <v-icon>mdi-delete</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </v-card>

    <CreateMedicationmain
      v-model="showCreateDialog"
      @created="handleMedicationCreated"
    />

    <EditMedicationDialog
      v-model="showEditDialog"
      :medication="selectedMedication"
      @updated="handleMedicationUpdated"
    />

    <DeleteMedicationDialog
      v-model="showDeleteDialog"
      :medication="selectedMedication"
      @deleted="handleMedicationDeleted"
    />
  </div>
</template>

<script>
import CreateMedicationmain from "@/components/DrugLibrary/Dialog/CreateMedicationmain.vue";
import EditMedicationDialog from "@/components/DrugLibrary/Dialog/EditMedicationDialog.vue";
import DeleteMedicationDialog from "@/components/DrugLibrary/Dialog/DeleteMedicationDialog.vue";
import GetMedications from "@/mixins/Medication/GetMedications";
import GetMedicationCategories from "@/mixins/Medication/GetMedicationCategories";
import { formatMedicationCategoryName } from "@/utils/medicationCategoryLabel";
import GetMedicationClasses from "@/mixins/Medication/GetMedicationClasses";

export default {
  name: "MedicationMain",
  mixins: [GetMedications, GetMedicationCategories, GetMedicationClasses],
  components: {
    CreateMedicationmain,
    EditMedicationDialog,
    DeleteMedicationDialog,
  },
  data() {
    return {
      showCreateDialog: false,
      showEditDialog: false,
      showDeleteDialog: false,
      tableLoading: false,
      selectedMedication: null,
      selectedIndex: -1,
      medications: [],
      search: "",
      selectedCategoryId: null,
      selectedSubCategoryId: null,
      windowHeight: window.innerHeight,
      categories: [],
      headers: [
        { title: "Medication Name", key: "medication_name", sortable: true },
        {
          title: "Medication Category",
          key: "medication_category_name",
          sortable: true,
        },
        { title: "Medication Class", key: "medication_class", sortable: true },
        { title: "Brand Name", key: "brand_name", sortable: true },
        { title: "Actions", key: "actions", sortable: false, align: "center" },
      ],
    };
  },
  computed: {
    noDataText() {
      return "No medications found. Click 'Create Medication' to add one.";
    },
    tableHeight() {
      return this.windowHeight - 280;
    },
    categoryOptions() {
      return this.categories;
    },
    filteredMedicationClassOptions() {
      if (!this.selectedCategoryId) {
        return [];
      }
      return this.medicationClassesList.filter(
        (item) =>
          String(item.medication_category_id) === String(this.selectedCategoryId)
      );
    },
    hasMedicationClasses() {
      return this.filteredMedicationClassOptions.length > 0;
    },
    filteredMedications() {
      let list = this.medications;

      if (this.selectedSubCategoryId) {
        list = list.filter(
          (med) =>
            String(med.medication_class_id) === String(this.selectedSubCategoryId)
        );
      } else if (this.selectedCategoryId) {
        const allowedClassIds = this.filteredMedicationClassOptions.map((item) =>
          String(item.class_id)
        );
        list = list.filter((med) =>
          allowedClassIds.includes(String(med.medication_class_id))
        );
      }

      if (!this.search) {
        return list;
      }

      const searchTerm = this.search.toLowerCase().trim();
      return list.filter((med) => {
        return (
          (med.medication_name &&
            med.medication_name.toLowerCase().includes(searchTerm)) ||
          (med.medication_category_name &&
            String(formatMedicationCategoryName(med.medication_category_name)).toLowerCase().includes(searchTerm)) ||
          (med.medication_class &&
            med.medication_class.toLowerCase().includes(searchTerm)) ||
          (med.brand_name && med.brand_name.toLowerCase().includes(searchTerm))
        );
      });
    },
  },
  async mounted() {
    this.updateWindowHeight();
    window.addEventListener("resize", this.updateWindowHeight);
    await this.loadCategories();
    await this.loadMedicationClasses();
    await this.loadMedications();
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.updateWindowHeight);
  },
  methods: {
    formatMedicationCategoryName,
    onCategoryChange() {
      this.selectedSubCategoryId = null;
    },
    onTabActivated() {
      this.loadCategories();
      this.loadMedicationClasses();
      this.loadMedications();
    },
    async loadCategories() {
      try {
        await this.fetchMedicationCategories();
        this.categories = this.medicationCategoriesList.map((item) => ({
          category_id: item.medication_category_id,
          category_name: item.medication_category_name,
        }));
      } catch (error) {
        console.error("Error loading medication categories:", error);
      }
    },
    async loadMedicationClasses() {
      try {
        await this.fetchMedicationClasses();
      } catch (error) {
        console.error("Error loading medication classes:", error);
      }
    },
    async loadMedications() {
      this.tableLoading = true;
      try {
        await this.fetchMedications();
        this.medications = this.medicationsList.map((medication) =>
          this.enrichMedicationCategory(medication)
        );
      } catch (error) {
        console.error("Error loading medications:", error);
      } finally {
        this.tableLoading = false;
      }
    },
    enrichMedicationCategory(medication) {
      if (medication.medication_category_name) {
        return medication;
      }

      const matchedClass = this.medicationClassesList.find(
        (medClass) =>
          String(medClass.class_id) === String(medication.medication_class_id) ||
          (medClass.class_name || "").toLowerCase() ===
            (medication.medication_class || "").toLowerCase()
      );

      const matchedCategory = this.categories.find(
        (category) =>
          String(category.category_id) ===
          String(
            medication.medication_category_id ||
              matchedClass?.medication_category_id
          )
      );

      return {
        ...medication,
        medication_category_id:
          medication.medication_category_id ||
          matchedClass?.medication_category_id ||
          matchedCategory?.category_id ||
          "",
        medication_category_name:
          medication.medication_category_name ||
          matchedClass?.medication_category_name ||
          matchedCategory?.category_name ||
          "",
      };
    },
    updateWindowHeight() {
      this.windowHeight = window.innerHeight;
    },
    openCreateDialog() {
      this.showCreateDialog = true;
    },
    async handleMedicationCreated() {
      await this.loadMedications();
    },
    editMedication(med) {
      this.selectedMedication = { ...med };
      this.selectedIndex = this.medications.findIndex(
        (m) => m.medication_master_id === med.medication_master_id
      );
      this.showEditDialog = true;
    },
    async handleMedicationUpdated() {
      await this.loadMedications();
      this.selectedIndex = -1;
      this.selectedMedication = null;
    },
    deleteMedication(med) {
      this.selectedMedication = { ...med };
      this.selectedIndex = this.medications.findIndex(
        (m) => m.medication_master_id === med.medication_master_id
      );
      this.showDeleteDialog = true;
    },
    async handleMedicationDeleted() {
      await this.loadMedications();
      this.selectedIndex = -1;
      this.selectedMedication = null;
    },
  },
};
</script>
